import { CommonModule } from '@angular/common';
import { Component, inject, input, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideMail } from '@ng-icons/lucide';
import { ErrorStateMatcher } from '@spartan-ng/brain/forms';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmErrorDirective, HlmFormFieldComponent } from '@spartan-ng/ui-formfield-helm';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import { toast } from 'ngx-sonner';
import { NewsletterService } from '../../services/newsletter.service';

export class ShowOnSubmitErrorStateMatcher implements ErrorStateMatcher {
  isInvalid(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && isSubmitted);
  }
}

interface SubscribeForm {
  email: FormControl<string>;
}

@Component({
  selector: 'app-newsletter-subscribe',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HlmFormFieldComponent,
    HlmInputDirective,
    HlmButtonDirective,
    HlmErrorDirective,
  ],
  providers: [provideIcons({ lucideMail }), { provide: ErrorStateMatcher, useClass: ShowOnSubmitErrorStateMatcher }],
  templateUrl: './newsletter-subscribe.component.html',
  styleUrl: './newsletter-subscribe.component.scss',
})
export class NewsletterSubscribeComponent {
  private newsletterService = inject(NewsletterService);
  private router = inject(Router);

  @ViewChild(FormGroupDirective) private formDirective!: FormGroupDirective;

  redirectToConfirmPage = input(false);

  subscribeForm: FormGroup<SubscribeForm>;
  isSubmitting = false;

  constructor() {
    this.subscribeForm = new FormGroup<SubscribeForm>({
      email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    });
  }

  get emailControl() {
    return this.subscribeForm.controls.email;
  }

  onSubmit() {
    if (this.subscribeForm.invalid) {
      // The form will be marked as submitted by the ngSubmit directive,
      // and our ShowOnSubmitErrorStateMatcher will then show errors.
      return;
    }

    this.isSubmitting = true;
    const email = this.emailControl.value;

    this.newsletterService.subscribe(email).subscribe({
      next: () => {
        // Changed _response to ()
        this.isSubmitting = false;

        toast.success('Check your inbox to confirm your subscription.');

        // Reset form value, clear validation state, and reset submitted status
        if (this.formDirective) {
          this.formDirective.resetForm();
        }
        // Ensure the input field is cleared
        this.subscribeForm.patchValue({ email: '' });

        if (this.redirectToConfirmPage()) {
          this.router.navigate(['/subscribe']);
        }
      },
      error: error => {
        this.isSubmitting = false;
        toast.error(error.error?.error || 'Failed to subscribe. Please try again later.');

        // Reset form submission state without clearing input to allow the user to correct their entry
        if (this.formDirective) {
          // Reset submitted flag but preserve form values
          this.formDirective.resetForm({ email: this.emailControl.value });
        }
      },
    });
  }
}
